import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Button,
  Box,
  CircularProgress,
  TextField,
} from "@mui/material";
import { getTeamDetails, updateTeam } from "../../api/api"; // API calls

const TeamDetails = () => {
  const { teamId } = useParams();
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");
  const [newDescription, setNewDescription] = useState("");

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const teamData = await getTeamDetails(teamId);
        setTeam(teamData);
        setNewTeamName(teamData.name);
        setNewDescription(teamData.description);
      } catch (error) {
        console.error("Error fetching team details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTeam();
  }, [teamId]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      await updateTeam(teamId, { name: newTeamName, description: newDescription });
      setTeam({ ...team, name: newTeamName, description: newDescription });
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating team:", error);
    }
  };

  if (loading) {
    return (
      <Container sx={{ textAlign: "center", mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 4 }}>
      {/* Team Details */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5">Team Details</Typography>
          {isEditing ? (
            <>
              <TextField
                fullWidth
                label="Team Name"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Description"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                multiline
                rows={3}
                sx={{ mb: 2 }}
              />
              <Button variant="contained" color="primary" onClick={handleSave}>
                Save
              </Button>
            </>
          ) : (
            <>
              <Typography variant="body1">
                <strong>Name:</strong> {team.name}
              </Typography>
              <Typography variant="body1">
                <strong>Description:</strong> {team.description || "No description available"}
              </Typography>
              <Typography variant="body1">
                <strong>Created By:</strong> {team.createdBy}
              </Typography>
              {team.canEdit && (
                <Button variant="contained" color="primary" onClick={handleEdit} sx={{ mt: 2 }}>
                  Edit Team
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Team Members */}
      <Card>
        <CardContent>
          <Typography variant="h5">Team Members</Typography>
          {team.members.length > 0 ? (
            <List>
              {team.members.map((member) => (
                <ListItem key={member.id}>
                  <ListItemText primary={member.name} secondary={`Role: ${member.role}`} />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2">No members in this team.</Typography>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default TeamDetails;
